import React, { useState, useEffect } from "react";
import {
  Card,
  Row,
  Col,
  Typography,
  Table,
  Button,
  Alert,
  Space,
  Tag,
  message,
  Spin,
  Tabs,
  Tooltip,
  Empty,
  Input,
  DatePicker,
  Select,
} from "antd";
import {
  FileTextOutlined,
  DownloadOutlined,
  CreditCardOutlined,
  FilePdfOutlined,
  SearchOutlined,
  FilterOutlined,
  ExportOutlined,
  CopyOutlined,
  ClockCircleOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { useAuth0 } from "@auth0/auth0-react";
import { useOrganization } from "../../contexts/organization-context";
import { InvoiceService, Invoice, Payment, UpcomingInvoicesResponse } from "../../services/invoiceService";
import { InvoiceDetailsModal } from "../../components/InvoiceDetailsModal";
import type { ColumnsType } from 'antd/es/table';

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { RangePicker } = DatePicker;

export const Invoices: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [upcomingData, setUpcomingData] = useState<UpcomingInvoicesResponse | null>(null);
  const [filteredInvoices, setFilteredInvoices] = useState<Invoice[]>([]);
  const [filteredPayments, setFilteredPayments] = useState<Payment[]>([]);
  const [downloadingInvoices, setDownloadingInvoices] = useState<Set<string>>(new Set());
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [dateRange, setDateRange] = useState<[any, any] | null>(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  
  const { getAccessTokenSilently } = useAuth0();
  const { ownedOrganizations, isOrganizationOwner } = useOrganization();

  const fetchInvoiceData = async () => {
    if (!isOrganizationOwner || ownedOrganizations.length === 0) return;

    try {
      setLoading(true);
      const token = await getAccessTokenSilently();
      const organizationId = ownedOrganizations[0].id;
      
      const [invoicesData, paymentsData, upcomingData] = await Promise.allSettled([
        InvoiceService.getOrganizationInvoices(organizationId, token),
        InvoiceService.getOrganizationPayments(organizationId, token),
        InvoiceService.getUpcomingInvoices(organizationId, token)
      ]);
      
      if (invoicesData.status === 'fulfilled') {
        // Filter out draft invoices - they are incomplete and shouldn't be shown to users
        const filteredInvoices = invoicesData.value.filter(invoice => invoice.status !== 'draft');
        setInvoices(filteredInvoices);
        setFilteredInvoices(filteredInvoices);
      } else {
        console.error("Error fetching invoices:", invoicesData.reason);
        message.error("Failed to load invoices");
      }

      if (paymentsData.status === 'fulfilled') {
        setPayments(paymentsData.value);
        setFilteredPayments(paymentsData.value);
      } else {
        console.error("Error fetching payments:", paymentsData.reason);
        message.error("Failed to load payments");
      }

      if (upcomingData.status === 'fulfilled') {
        setUpcomingData(upcomingData.value);
      } else {
        console.error("Error fetching upcoming invoices:", upcomingData.reason);
        message.warning("Failed to load upcoming invoices");
      }

    } catch (error) {
      console.error("Error fetching data:", error);
      message.error("Failed to load billing data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoiceData();
  }, [isOrganizationOwner, ownedOrganizations]);

  // Filter invoices based on search and filters
  useEffect(() => {
    let filtered = [...invoices];

    // Search filter
    if (searchText) {
      filtered = filtered.filter(invoice => 
        invoice.number?.toLowerCase().includes(searchText.toLowerCase()) ||
        invoice.description?.toLowerCase().includes(searchText.toLowerCase()) ||
        invoice.id.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter(invoice => invoice.status === statusFilter);
    }

    // Date range filter
    if (dateRange && dateRange[0] && dateRange[1]) {
      const startDate = dateRange[0].startOf('day').unix();
      const endDate = dateRange[1].endOf('day').unix();
      filtered = filtered.filter(invoice => 
        invoice.created >= startDate && invoice.created <= endDate
      );
    }

    setFilteredInvoices(filtered);
  }, [invoices, searchText, statusFilter, dateRange]);

  // Filter payments similarly
  useEffect(() => {
    let filtered = [...payments];

    if (searchText) {
      filtered = filtered.filter(payment => 
        payment.description?.toLowerCase().includes(searchText.toLowerCase()) ||
        payment.id.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    if (statusFilter !== "all") {
      filtered = filtered.filter(payment => payment.status === statusFilter);
    }

    if (dateRange && dateRange[0] && dateRange[1]) {
      const startDate = dateRange[0].startOf('day').unix();
      const endDate = dateRange[1].endOf('day').unix();
      filtered = filtered.filter(payment => 
        payment.created >= startDate && payment.created <= endDate
      );
    }

    setFilteredPayments(filtered);
  }, [payments, searchText, statusFilter, dateRange]);

  const handleDownloadInvoice = async (invoiceId: string) => {
    if (!ownedOrganizations.length) return;

    try {
      setDownloadingInvoices(prev => new Set(prev).add(invoiceId));
      const token = await getAccessTokenSilently();
      
      const response = await InvoiceService.getInvoiceDownloadUrl(
        ownedOrganizations[0].id, 
        invoiceId, 
        token
      );
      
      // Open PDF in new tab
      window.open(response.download_url, '_blank');
      
    } catch (error: any) {
      console.error("Error downloading invoice:", error);
      message.error(error.response?.data?.detail || "Failed to download invoice");
    } finally {
      setDownloadingInvoices(prev => {
        const newSet = new Set(prev);
        newSet.delete(invoiceId);
        return newSet;
      });
    }
  };

  const handleCopyToClipboard = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text);
      message.success(`${type} copied to clipboard`);
    } catch (error) {
      console.error("Failed to copy to clipboard:", error);
      message.error("Failed to copy to clipboard");
    }
  };

  const handleShowInvoiceDetails = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setDetailsModalOpen(true);
  };

  const handleCloseDetailsModal = () => {
    setDetailsModalOpen(false);
    setSelectedInvoice(null);
  };

  const invoiceColumns: ColumnsType<Invoice> = [
    {
      title: 'Invoice #',
      dataIndex: 'number',
      key: 'number',
      width: 200,
      render: (number, record) => {
        const displayValue = number || record.id;
        return (
          <Space direction="vertical" size={0}>
            <Space size={4}>
              <Text 
                strong 
                style={{ 
                  fontFamily: 'monospace', 
                  fontSize: '13px',
                  cursor: 'pointer',
                  color: '#1890ff'
                }}
                onClick={() => handleCopyToClipboard(displayValue, 'Invoice number')}
                title="Click to copy"
              >
                {displayValue}
              </Text>
              <Tooltip title="Copy to clipboard">
                <Button
                  type="text"
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() => handleCopyToClipboard(displayValue, 'Invoice number')}
                  style={{ padding: '0 4px', height: '16px', minWidth: '16px' }}
                />
              </Tooltip>
            </Space>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {InvoiceService.formatDate(record.created)}
            </Text>
          </Space>
        );
      },
    },
    {
      title: 'Amount',
      key: 'amount',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{InvoiceService.formatCurrency(record.amount_due, record.currency)}</Text>
          {record.amount_paid > 0 && (
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Paid: {InvoiceService.formatCurrency(record.amount_paid, record.currency)}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={InvoiceService.getStatusColor(status)}>
          {status.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Due Date',
      dataIndex: 'due_date',
      key: 'due_date',
      render: (due_date) => due_date ? InvoiceService.formatDate(due_date) : 'N/A',
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (description) => description || 'Subscription charge',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          {record.invoice_pdf && (
            <Tooltip title="Download PDF">
              <Button
                type="primary"
                ghost
                size="small"
                icon={<DownloadOutlined />}
                loading={downloadingInvoices.has(record.id)}
                onClick={() => handleDownloadInvoice(record.id)}
              >
                PDF
              </Button>
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  const paymentColumns: ColumnsType<Payment> = [
    {
      title: 'Payment ID',
      dataIndex: 'id',
      key: 'id',
      width: 200,
      render: (id, record) => (
        <Space direction="vertical" size={0}>
          <Space size={4}>
            <Text 
              strong 
              style={{ 
                fontFamily: 'monospace', 
                fontSize: '13px',
                cursor: 'pointer',
                color: '#1890ff'
              }}
              onClick={() => handleCopyToClipboard(id, 'Payment ID')}
              title="Click to copy"
            >
              {id}
            </Text>
            <Tooltip title="Copy to clipboard">
              <Button
                type="text"
                size="small"
                icon={<CopyOutlined />}
                onClick={() => handleCopyToClipboard(id, 'Payment ID')}
                style={{ padding: '0 4px', height: '16px', minWidth: '16px' }}
              />
            </Tooltip>
          </Space>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {InvoiceService.formatDate(record.created)}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount, record) => (
        <Text strong>{InvoiceService.formatCurrency(amount, record.currency)}</Text>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={InvoiceService.getStatusColor(status)}>
          {status.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Payment Method',
      key: 'payment_method',
      render: (_, record) => {
        if (record.payment_method_details) {
          const card = record.payment_method_details;
          return (
            <Space>
              <CreditCardOutlined />
              <Text>**** {card.last4}</Text>
              <Text type="secondary">({card.brand?.toUpperCase()})</Text>
            </Space>
          );
        }
        return 'N/A';
      },
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (description) => description || 'Payment',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          {record.receipt_url && (
            <Tooltip title="View Receipt">
              <Button
                type="primary"
                ghost
                size="small"
                icon={<FilePdfOutlined />}
                onClick={() => window.open(record.receipt_url, '_blank')}
              >
                Receipt
              </Button>
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  const upcomingColumns: ColumnsType<Invoice> = [
    {
      title: 'Type',
      key: 'type',
      width: 150,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>
            {record.is_pending_summary ? 'Pending Charges' : 'Next Invoice'}
          </Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.is_pending_summary 
              ? 'To be charged' 
              : InvoiceService.formatDate(record.created)
            }
          </Text>
        </Space>
      ),
    },
    {
      title: 'Amount',
      key: 'amount',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{InvoiceService.formatCurrency(record.amount_due, record.currency)}</Text>
          {record.amount_remaining > 0 && record.amount_remaining !== record.amount_due && (
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Remaining: {InvoiceService.formatCurrency(record.amount_remaining, record.currency)}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status, record) => (
        <Tag color={InvoiceService.getUpcomingStatusColor(status)}>
          {InvoiceService.getUpcomingStatusText(record)}
        </Tag>
      ),
    },
    {
      title: 'Due Date',
      dataIndex: 'due_date',
      key: 'due_date',
      render: (due_date, record) => {
        if (record.is_pending_summary) {
          return <Text type="secondary">When trial ends</Text>;
        }
        
        // For upcoming invoices, prefer next_payment_attempt over due_date
        const billingDate = record.next_payment_attempt || due_date;
        if (billingDate) {
          return (
            <Space direction="vertical" size={0}>
              <Text>{InvoiceService.formatDate(billingDate)}</Text>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Next billing cycle
              </Text>
            </Space>
          );
        }
        
        return <Text type="secondary">On schedule</Text>;
      },
    },
    {
      title: 'Description',
      key: 'description',
      ellipsis: true,
      render: (_, record) => (
        <Tooltip title={InvoiceService.getUpcomingDescription(record)}>
          <Text>{InvoiceService.getUpcomingDescription(record)}</Text>
        </Tooltip>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          {InvoiceService.hasLineItemDetails(record) && (
            <Tooltip title="View detailed breakdown">
              <Button
                type="default"
                size="small"
                icon={<EyeOutlined />}
                onClick={() => handleShowInvoiceDetails(record)}
              >
                Details
              </Button>
            </Tooltip>
          )}
          {!record.is_pending_summary && record.invoice_pdf && (
            <Tooltip title="Download PDF">
              <Button
                type="primary"
                ghost
                size="small"
                icon={<DownloadOutlined />}
                loading={downloadingInvoices.has(record.id)}
                onClick={() => handleDownloadInvoice(record.id)}
              >
                PDF
              </Button>
            </Tooltip>
          )}
          {record.is_pending_summary && !InvoiceService.hasLineItemDetails(record) && (
            <Tooltip title="These charges are pending">
              <Button
                type="text"
                size="small"
                icon={<ClockCircleOutlined />}
                disabled
              >
                Pending
              </Button>
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  const clearFilters = () => {
    setSearchText("");
    setStatusFilter("all");
    setDateRange(null);
  };

  if (!isOrganizationOwner) {
    return (
      <Card>
        <Alert
          message="Access Denied"
          description="Only organization owners can view invoices."
          type="error"
          showIcon
        />
      </Card>
    );
  }

  if (loading) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>
        <FileTextOutlined /> Invoices & Billing
      </Title>
      
      {/* Filters */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={8} md={6}>
            <Input
              placeholder="Search invoices..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={8} md={6}>
            <Select
              style={{ width: '100%' }}
              placeholder="Filter by status"
              value={statusFilter}
              onChange={setStatusFilter}
            >
              <Select.Option value="all">All Statuses</Select.Option>
              <Select.Option value="paid">Paid</Select.Option>
              <Select.Option value="succeeded">Succeeded</Select.Option>
              <Select.Option value="open">Open</Select.Option>
              <Select.Option value="pending">Pending</Select.Option>
              <Select.Option value="failed">Failed</Select.Option>
            </Select>
          </Col>
          <Col xs={24} sm={8} md={6}>
            <RangePicker
              style={{ width: '100%' }}
              value={dateRange}
              onChange={setDateRange}
              placeholder={['Start Date', 'End Date']}
            />
          </Col>
          <Col xs={24} sm={24} md={6}>
            <Space>
              <Button 
                icon={<FilterOutlined />} 
                onClick={clearFilters}
                disabled={!searchText && statusFilter === 'all' && !dateRange}
              >
                Clear Filters
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      <Tabs defaultActiveKey="invoices">
        <TabPane
          tab={
            <span>
              <FileTextOutlined />
              Invoices ({filteredInvoices.length})
            </span>
          }
          key="invoices"
        >
          <Card>
            <Table
              columns={invoiceColumns}
              dataSource={filteredInvoices}
              rowKey="id"
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => 
                  `${range[0]}-${range[1]} of ${total} invoices`,
              }}
              locale={{
                emptyText: (
                  <Empty
                    description="No invoices found"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                ),
              }}
            />
          </Card>
        </TabPane>

        <TabPane
          tab={
            <span>
              <CreditCardOutlined />
              Payments ({filteredPayments.length})
            </span>
          }
          key="payments"
        >
          <Card>
            <Table
              columns={paymentColumns}
              dataSource={filteredPayments}
              rowKey="id"
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => 
                  `${range[0]}-${range[1]} of ${total} payments`,
              }}
              locale={{
                emptyText: (
                  <Empty
                    description="No payments found"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                ),
              }}
            />
          </Card>
        </TabPane>

        <TabPane
          tab={
            <span>
              <ClockCircleOutlined />
              Upcoming ({upcomingData?.upcoming_invoices.length || 0})
            </span>
          }
          key="upcoming"
        >
          <Card>
            {upcomingData?.upcoming_invoices.length === 0 && (
              <Alert
                message="No Upcoming Invoices"
                description="No upcoming charges or invoices found for your account."
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}
            <Table
              columns={upcomingColumns}
              dataSource={upcomingData?.upcoming_invoices || []}
              rowKey="id"
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => 
                  `${range[0]}-${range[1]} of ${total} upcoming invoices`,
              }}
              locale={{
                emptyText: (
                  <Empty
                    description="No upcoming invoices found"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                ),
              }}
            />
          </Card>
        </TabPane>
      </Tabs>

      <InvoiceDetailsModal
        open={detailsModalOpen}
        onCancel={handleCloseDetailsModal}
        invoice={selectedInvoice}
      />
    </div>
  );
}; 